/**
 * Class Selector Component
 * Allows players to select their character class for a quest
 */

import React from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonBadge,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
} from '@ionic/react';
import { checkmarkCircle } from 'ionicons/icons';
import { PlayerClass } from '../../../types/pipsGuildQuest';
import './ClassSelector.css';

interface ClassSelectorProps {
  classes: PlayerClass[];
  selectedClassId?: string;
  onSelectClass: (classId: string) => void;
  disabled?: boolean;
}

const ClassSelector: React.FC<ClassSelectorProps> = ({
  classes,
  selectedClassId,
  onSelectClass,
  disabled = false,
}) => {
  const getClassIcon = (className: string): string => {
    const icons: Record<string, string> = {
      Warrior: '⚔️',
      Mage: '🔮',
      Rogue: '🗡️',
      Cleric: '⚕️',
      Ranger: '🏹',
      Paladin: '🛡️',
      Druid: '🌿',
      Necromancer: '💀',
      Warlock: '🔥',
    };
    return icons[className] || '⭐';
  };

  return (
    <div className="class-selector">
      <h3>Choose Your Class</h3>
      <div className="class-grid">
        {classes.map((classData) => {
          const isSelected = selectedClassId === classData.id;
          // Database returns snake_case field names
          const baseStats = (classData as any).base_stats || classData.baseStats || {};
          const baseHp = (classData as any).base_hp || classData.baseHp || 0;
          const isStarter = (classData as any).is_starter ?? classData.isStarter ?? false;

          return (
            <IonCard
              key={classData.id}
              className={`class-card ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={() => !disabled && onSelectClass(classData.id)}
              button={!disabled}
            >
              {isSelected && (
                <div className="selected-badge">
                  <IonIcon icon={checkmarkCircle} />
                </div>
              )}

              <div className="class-icon">{getClassIcon(classData.name)}</div>

              <IonCardHeader>
                <IonCardTitle>{classData.name}</IonCardTitle>
                {!isStarter && (
                  <IonBadge color="warning" className="unlock-badge">
                    Unlockable
                  </IonBadge>
                )}
              </IonCardHeader>

              <IonCardContent>
                <p className="class-description">{classData.description}</p>

                <div className="stats-summary">
                  <div className="stat-row">
                    <span className="stat-label">HP:</span>
                    <span className="stat-value">{baseHp}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">ATK:</span>
                    <span className="stat-value">{baseStats?.atk || 0}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">DEF:</span>
                    <span className="stat-value">{baseStats?.def || 0}</span>
                  </div>
                </div>

                <div className="abilities-preview">
                  <strong>Abilities:</strong>
                  <IonList className="ability-list">
                    {(classData.abilities as any[]).slice(0, 3).map((ability: any, idx: number) => (
                      <IonItem key={idx} lines="none" className="ability-item">
                        <IonLabel>
                          <p className="ability-name">{ability.name}</p>
                          <p className="ability-desc">{ability.description}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                </div>
              </IonCardContent>
            </IonCard>
          );
        })}
      </div>
    </div>
  );
};

export default ClassSelector;
